import { _any } from "i18n";
import { getApiErrorMessage, parseMessageAttribute } from "libs/utils";

const VideoServerError = {
	AlreadyDeleted: "VIDEO IS IN VIDEO_DELETED",
	AlreadyDeletedQueue: "VIDEO IS IN VIDEO_DELETE",
	NotFound: "NOT_FOUND",
	IngestSourceNotFound: "INGEST: SOURCE NOT FOUND",
} as const;

const videoServerErrorCopy: Record<string, { key: string; fallback: string }> = {
	[VideoServerError.AlreadyDeleted]: {
		key: "admin.videoUploads.error.alreadyDeleted",
		fallback: "This video has already been deleted, so this action cannot be completed.",
	},
	[VideoServerError.AlreadyDeletedQueue]: {
		key: "admin.videoUploads.error.alreadyDeleted",
		fallback: "This video has already been deleted, so this action cannot be completed.",
	},
	[VideoServerError.NotFound]: {
		key: "admin.videoUploads.error.notFound",
		fallback: "This video could not be found. It may have already been deleted.",
	},
	[VideoServerError.IngestSourceNotFound]: {
		key: "admin.videoUploads.error.ingestSourceNotFound",
		fallback: "The ingest source for this video could not be found.",
	},
};

const genericVideoUploadError = {
	key: "admin.videoUploads.error.generic",
	fallback: "The video server could not complete this request.",
};

function translateVideoUploadError(key: string, fallback: string): string {
	const translated = _any(key);
	if (translated === key) {
		return fallback;
	}

	return translated;
}

function messageFromParsedBody(parsed: unknown): string | undefined {
	if (typeof parsed !== "object" || parsed === null) {
		return undefined;
	}

	const body = parsed as Record<string, unknown>;
	if (typeof body.message === "string" && body.message !== "") {
		return body.message;
	}

	if (typeof body.error === "string" && body.error !== "") {
		return body.error;
	}

	return undefined;
}

function messageFromJsonString(value: string): string | undefined {
	const trimmed = value.trim();
	if (trimmed.startsWith("{") === false && trimmed.startsWith("[") === false) {
		return undefined;
	}

	try {
		return messageFromParsedBody(JSON.parse(trimmed));
	} catch {
		return undefined;
	}
}

function extractVideoServerError(error: unknown): string {
	if (typeof error === "string") {
		return messageFromJsonString(error) ?? getApiErrorMessage(error);
	}

	const fromAttributes = parseMessageAttribute(error);
	return messageFromJsonString(fromAttributes) ?? fromAttributes;
}

/**
 * Maps raw video-server codes (e.g. VIDEO IS IN VIDEO_DELETED, {"error":"NOT_FOUND"})
 * to admin-facing copy. Localization keys (e.g. game.not.found) are resolved via i18n.
 * Unknown errors stay as the extracted text, or `fallback`.
 */
export function getVideoUploadErrorMessage(error: unknown, fallback?: string): string {
	const raw = extractVideoServerError(error).trim();
	const mapped = videoServerErrorCopy[raw.toUpperCase()];
	if (mapped !== undefined) {
		return translateVideoUploadError(mapped.key, mapped.fallback);
	}

	if (raw !== "" && raw.startsWith("{") === false && raw.startsWith("[") === false) {
		return translateVideoUploadError(raw, raw);
	}

	if (fallback !== undefined && fallback !== "") {
		return fallback;
	}

	return translateVideoUploadError(genericVideoUploadError.key, genericVideoUploadError.fallback);
}
